import {
  type CSSProperties,
  memo,
  useMemo,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

export type ListKey = string | number;

export interface ListLabels {
  selectAll?: string;
  deselectAll?: string;
  selectItem?: string;
  delete?: string;
  deleteSelection?: string;
  deleting?: string;
}

export interface ListColumn {
  key: string;
  label: string;
  width: string;
  align?: "left" | "center" | "right";
}

export interface ListRenderContext {
  columns: readonly ListColumn[];
  gridStyle: CSSProperties;
  isMobile: boolean;
  getCellClassName: (column: ListColumn) => string;
}

export interface ListProps<T> extends Omit<
  HTMLAttributes<HTMLUListElement>,
  "children"
> {
  heightMode?: "container" | "content";
  scrollMode?: "internal" | "none";
  density?: "comfortable" | "compact";
  items: readonly T[];
  columns: readonly ListColumn[];
  renderItem: (
    item: T,
    index: number,
    isSelected: boolean,
    context: ListRenderContext,
  ) => ReactNode;
  renderMobileItem?: (
    item: T,
    index: number,
    isSelected: boolean,
    context: ListRenderContext,
  ) => ReactNode;
  keyExtractor: (item: T, index: number) => ListKey;
  selectedKeys?: readonly ListKey[] | ReadonlySet<ListKey>;
  onToggleSelect?: (key: ListKey) => void;
  onBulkDelete?: (keys: ListKey[]) => void;
  /** Placeholder for future windowed rendering support (react-window/react-virtualized). */
  virtualized?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  LoadingComponent?: ReactNode;
  EmptyComponent?: ReactNode;
  ErrorComponent?: ReactNode;
  isBulkActionLoading?: boolean;
  labels?: ListLabels;
  topContent?: ReactNode;
  footer?: ReactNode;
  extraActions?: (selectedKeys: ListKey[]) => ReactNode;
  itemClassName?: string;
  stateItemClassName?: string;
}

const ListBase = <T,>({
  heightMode = "container",
  scrollMode = "internal",
  density = "comfortable",
  items,
  columns,
  renderItem,
  renderMobileItem,
  keyExtractor,
  selectedKeys,
  onToggleSelect,
  onBulkDelete,
  virtualized = false,
  isLoading = false,
  isError = false,
  isEmpty,
  LoadingComponent,
  EmptyComponent,
  ErrorComponent,
  isBulkActionLoading = false,
  labels,
  topContent,
  footer,
  extraActions,
  className,
  itemClassName,
  stateItemClassName,
  "aria-busy": ariaBusy,
  ...props
}: ListProps<T>) => {
  const { t } = useTranslation(["common"] as const);

  // For best memoization results with React.memo, parent components should memoize
  // `renderItem` and `onToggleSelect` with useCallback so function references are stable.

  const selectedSet = useMemo<ReadonlySet<ListKey>>(() => {
    if (!selectedKeys) {
      return new Set();
    }
    return selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys);
  }, [selectedKeys]);

  const computedIsEmpty = isEmpty ?? items.length === 0;
  const selectionEnabled = Boolean(onToggleSelect);
  const visibleKeys = useMemo(
    () => items.map((item, index) => keyExtractor(item, index)),
    [items, keyExtractor],
  );
  const selectedVisibleCount = useMemo(
    () => visibleKeys.filter((key) => selectedSet.has(key)).length,
    [visibleKeys, selectedSet],
  );
  const hasSelection = selectedVisibleCount > 0;
  const totalSelectedCount = selectedSet.size;
  const selectAllLabel =
    labels?.selectAll ??
    t("common:actions.selectAll", { defaultValue: "Select all" });
  const deselectAllLabel =
    labels?.deselectAll ??
    t("common:actions.deselectAll", { defaultValue: "Deselect all" });
  const deleteSelectionLabel =
    labels?.deleteSelection ??
    t("common:actions.deleteSelection", { defaultValue: "Delete selection" });
  const deleteSelectionCountLabel =
    labels?.deleteSelection ??
    t("common:actions.deleteSelectionCount", {
      count: totalSelectedCount,
      defaultValue: "Delete ({{count}}) items",
    });
  const deletingLabel =
    labels?.deleting ??
    t("common:actions.deleting", { defaultValue: "Deleting..." });
  const isAllSelected =
    visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
  const selectedKeysArray = useMemo(
    () => Array.from(selectedSet),
    [selectedSet],
  );
  const extraActionsNode = extraActions?.(selectedKeysArray);
  const gridTemplateColumns = useMemo(
    () => columns.map((column) => column.width).join(" "),
    [columns],
  );
  const gridStyle = useMemo<CSSProperties>(
    () => ({ gridTemplateColumns }),
    [gridTemplateColumns],
  );
  const getCellClassName = (column: ListColumn) => {
    if (column.align === "right") {
      return "min-w-0 text-right";
    }
    if (column.align === "center") {
      return "min-w-0 text-center";
    }
    return "min-w-0 text-left";
  };

  const desktopRenderContext: ListRenderContext = {
    columns,
    gridStyle,
    isMobile: false,
    getCellClassName,
  };

  const mobileRenderContext: ListRenderContext = {
    columns,
    gridStyle,
    isMobile: true,
    getCellClassName,
  };

  const renderState = () => {
    if (isLoading && LoadingComponent) {
      return (
        <li className={stateItemClassName} role="status" aria-live="polite">
          {LoadingComponent}
        </li>
      );
    }

    if (isError && ErrorComponent) {
      return (
        <li className={stateItemClassName} role="alert">
          {ErrorComponent}
        </li>
      );
    }

    if (computedIsEmpty && EmptyComponent) {
      return (
        <li className={stateItemClassName} role="status" aria-live="polite">
          {EmptyComponent}
        </li>
      );
    }

    return null;
  };

  const stateNode = renderState();
  const showHeaderRow = !stateNode && items.length > 0 && columns.length > 0;

  const handleItemKeyDown = (
    event: KeyboardEvent<HTMLLIElement>,
    key: ListKey,
  ) => {
    if (!onToggleSelect) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggleSelect(key);
    }
  };

  if (virtualized) {
    // Placeholder branch for future virtualization integration.
    // Keep default rendering for now to preserve behavior.
  }

  const handleSelectAll = () => {
    if (!onToggleSelect || visibleKeys.length === 0 || isBulkActionLoading) {
      return;
    }

    if (isAllSelected) {
      visibleKeys.forEach((key) => {
        if (selectedSet.has(key)) {
          onToggleSelect(key);
        }
      });
      return;
    }

    visibleKeys.forEach((key) => {
      if (!selectedSet.has(key)) {
        onToggleSelect(key);
      }
    });
  };

  const handleBulkDelete = () => {
    if (!onBulkDelete || !hasSelection || isBulkActionLoading) {
      return;
    }

    const selectedVisibleKeys = visibleKeys.filter((key) =>
      selectedSet.has(key),
    );
    onBulkDelete(selectedVisibleKeys);
  };

  const containerClass = clsx(
    "flex min-h-0 min-w-0 flex-col rounded-lg bg-surface-primary",
    heightMode === "container" && "h-full",
    className,
  );

  const bodyClass = clsx(
    "min-h-0 min-w-0 flex-1",
    scrollMode === "internal" && "overflow-auto",
  );

  const rowClassName = clsx(
    "rounded-xl border border-border-subtle bg-surface-primary px-4 transition-colors",
    density === "comfortable" ? "py-4" : "py-3",
    selectionEnabled && "hover:border-primary/40 hover:bg-surface-secondary/60",
  );

  return (
    <div className={containerClass}>
      {topContent ? (
        <div className="w-full border-b border-border-subtle bg-surface-primary">
          {topContent}
        </div>
      ) : null}

      {selectionEnabled ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle bg-surface-primary px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sm font-medium text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content-primary disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSelectAll}
              disabled={visibleKeys.length === 0 || isBulkActionLoading}
            >
              {isAllSelected ? deselectAllLabel : selectAllLabel}
            </button>
            <span
              className="text-sm text-content-muted"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {t("common:selection.count", {
                count: totalSelectedCount,
                defaultValue:
                  totalSelectedCount === 1
                    ? "1 item selected"
                    : "{{count}} items selected",
              })}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {extraActionsNode}
            {onBulkDelete ? (
              hasSelection ? (
                <button
                  type="button"
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-state-danger transition-colors hover:bg-state-dangerSoft disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleBulkDelete}
                  disabled={isBulkActionLoading}
                >
                  {isBulkActionLoading
                    ? deletingLabel
                    : totalSelectedCount > 1
                      ? deleteSelectionCountLabel
                      : deleteSelectionLabel}
                </button>
              ) : (
                // i18n-disable: Invisible placeholder keeps toolbar width stable to prevent layout shift.
                <span className="invisible text-sm font-medium">
                  {deleteSelectionLabel}
                </span>
              )
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={bodyClass}>
        {showHeaderRow ? (
          <div
            className="hidden w-full min-w-0 items-center gap-4 border-b border-border-subtle bg-surface-primary px-4 py-3 text-xs font-semibold uppercase tracking-wide text-content-muted md:grid"
            style={gridStyle}
          >
            {columns.map((column) => (
              <span
                key={column.key}
                className={clsx("truncate", getCellClassName(column))}
              >
                {column.label}
              </span>
            ))}
          </div>
        ) : null}

        <ul
          className={clsx("pointer-events-auto min-w-0", className)}
          aria-busy={ariaBusy ?? isLoading}
          aria-label={selectionEnabled ? labels?.selectItem ?? selectAllLabel : undefined}
          aria-multiselectable={selectionEnabled ? true : undefined}
          role={selectionEnabled ? "listbox" : "list"}
          {...props}
        >
          {stateNode
            ? stateNode
            : items.map((item, index) => {
                const key = keyExtractor(item, index);
                const isSelected = selectedSet.has(key);

                return (
                  <li
                    key={key}
                    className={clsx(
                      rowClassName,
                      selectionEnabled && "cursor-pointer",
                      itemClassName,
                    )}
                    role={selectionEnabled ? "option" : "listitem"}
                    aria-selected={selectionEnabled ? isSelected : undefined}
                    data-selected={isSelected ? "true" : undefined}
                    onClick={
                      onToggleSelect ? () => onToggleSelect(key) : undefined
                    }
                    tabIndex={onToggleSelect ? 0 : undefined}
                    onKeyDown={(event) => handleItemKeyDown(event, key)}
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="hidden w-full min-w-0 items-center gap-4 md:grid"
                        style={gridStyle}
                      >
                        {renderItem(
                          item,
                          index,
                          isSelected,
                          desktopRenderContext,
                        )}
                      </div>

                      <div className="md:hidden">
                        {renderMobileItem ? (
                          renderMobileItem(
                            item,
                            index,
                            isSelected,
                            mobileRenderContext,
                          )
                        ) : (
                          <div className="flex flex-col gap-2">
                            {renderItem(
                              item,
                              index,
                              isSelected,
                              mobileRenderContext,
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
        </ul>
      </div>

      {footer ? (
        <div className="w-full border-t border-border-subtle bg-surface-primary">
          {footer}
        </div>
      ) : null}
    </div>
  );
};

export const List = memo(ListBase) as typeof ListBase;

export default List;
